<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="danger"
      />
      <span>{{ $t('involvedPanel.problems.loading') }}</span>
    </div>

    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="AlertCircleIcon"
            size="13"
            class="mr-25 text-danger"
          />
          {{ active.length ? $t('involvedPanel.problems.toolbar.count', { count: active.length }) : $t('involvedPanel.problems.toolbar.empty') }}
        </span>
        <button
          v-if="!readonly && available.length"
          class="btn btn-sm btn-danger"
          @click="openModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.problems.toolbar.newButton') }}
        </button>
      </div>

      <div
        v-if="!catalog.length"
        class="interc-empty"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.problems.empty.noCatalog') }}</p>
      </div>

      <div
        v-else-if="!active.length"
        class="interc-empty"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.problems.empty.noRecords') }}</p>
      </div>

      <div
        v-for="group in groups"
        :key="group.key"
        class="prob-group"
        :class="`prob-group--${group.key}`"
      >
        <div class="prob-group__title">
          {{ group.label }}
        </div>
        <div
          v-for="r in group.items"
          :key="r.id"
          class="prob-item"
        >
          <div class="prob-item__body">
            <div class="prob-item__head">
              <span class="prob-item__name">{{ problemLabel(r) }}</span>
              <span
                v-if="r.since"
                class="prob-item__since"
              >{{ $t('involvedPanel.problems.table.since', { value: formatSince(r.since) }) }}</span>
            </div>
            <div class="interc-item__meta">
              <feather-icon
                icon="UserIcon"
                size="11"
              />
              <span>{{ r.answered_by ? r.answered_by.name : '—' }}</span>
              <span class="interc-item__dot" />
              <feather-icon
                icon="ClockIcon"
                size="11"
              />
              <span>{{ r.answered_at | formatDate }}</span>
            </div>
          </div>
          <button
            v-if="!readonly"
            class="interc-item__del"
            :title="$t('involvedPanel.problems.table.deleteTitle')"
            :disabled="cancellingId === r.id"
            @click="cancel(r)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="13"
            />
          </button>
        </div>
      </div>

      <div
        v-if="history.length"
        class="prob-history"
      >
        <button
          type="button"
          class="prob-history__toggle"
          :aria-expanded="String(showHistory)"
          @click="showHistory = !showHistory"
        >
          <feather-icon
            :icon="showHistory ? 'ChevronDownIcon' : 'ChevronRightIcon'"
            size="14"
            class="mr-25"
          />
          {{ $t('involvedPanel.problems.history.toggle', { count: history.length, status: history.length === 1 ? $t('involvedPanel.problems.history.cancelledOne') : $t('involvedPanel.problems.history.cancelledMany') }) }}
        </button>
        <div
          v-if="showHistory"
          class="prob-history__list"
        >
          <div
            v-for="r in history"
            :key="r.id"
            class="prob-hist-item"
          >
            <div class="prob-item__head">
              <span
                class="prob-tag"
                :class="r.answer ? 'prob-tag--tem' : 'prob-tag--nega'"
              >{{ r.answer ? $t('involvedPanel.problems.table.tem') : $t('involvedPanel.problems.table.nega') }}</span>
              <span class="prob-item__name">{{ problemLabel(r) }}</span>
              <span
                v-if="r.since"
                class="prob-item__since"
              >{{ $t('involvedPanel.problems.table.since', { value: formatSince(r.since) }) }}</span>
            </div>
            <div class="interc-item__meta">
              {{ $t('involvedPanel.problems.history.registeredBy', { name: r.answered_by ? r.answered_by.name : '—', date: $options.filters.formatDate(r.answered_at) }) }}
              {{ $t('involvedPanel.problems.history.cancelledBy', { name: r.cancelled_by ? r.cancelled_by.name : '—', date: $options.filters.formatDate(r.cancelled_at) }) }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showModal"
      :title="$t('involvedPanel.problems.modal.title')"
      :title-class="'font-weight-bold'"
      size="md"
      centered
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <div class="form-group mb-2">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.problems.modal.problemLabel') }}</label>
        <v-select
          v-model="form.problem_id"
          :options="available"
          :reduce="p => p.id"
          label="label"
          :placeholder="$t('involvedPanel.problems.modal.problemPlaceholder')"
          class="vselect"
        />
      </div>
      <div class="form-group mb-2">
        <label class="interc-label"><span class="required">*</span> {{ $t('involvedPanel.problems.modal.patientLabel') }}</label>
        <div
          class="btn-group btn-group-sm d-flex"
          role="group"
        >
          <button
            type="button"
            class="btn"
            :class="form.answer === true ? 'btn-danger' : 'btn-outline-danger'"
            :aria-pressed="String(form.answer === true)"
            @click="setAnswer(true)"
          >
            {{ $t('involvedPanel.problems.modal.answerYes') }}
          </button>
          <button
            type="button"
            class="btn"
            :class="form.answer === false ? 'btn-secondary' : 'btn-outline-secondary'"
            :aria-pressed="String(form.answer === false)"
            @click="setAnswer(false)"
          >
            {{ $t('involvedPanel.problems.modal.answerNo') }}
          </button>
        </div>
      </div>
      <div
        v-if="form.answer === true"
        class="form-group mb-1"
      >
        <label
          class="interc-label"
          for="prob-since"
        >{{ $t('involvedPanel.problems.modal.sinceLabel') }} <small class="text-muted">{{ $t('involvedPanel.problems.modal.sinceOptional') }}</small></label>
        <input
          id="prob-since"
          :value="form.since"
          type="text"
          inputmode="numeric"
          class="form-control"
          :class="{ 'is-invalid': sinceMessage }"
          :placeholder="$t('involvedPanel.problems.modal.sincePlaceholder')"
          maxlength="7"
          autocomplete="off"
          @input="onSinceInput"
          @blur="sinceTouched = true"
        >
        <div
          v-if="sinceMessage"
          class="invalid-feedback d-block"
        >
          {{ sinceMessage }}
        </div>
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showModal = false"
        >
          {{ $t('involvedPanel.problems.modal.cancelButton') }}
        </button>
        <button
          class="btn btn-sm btn-danger"
          :disabled="saving || !canSave"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-25"
          />
          {{ $t('involvedPanel.problems.modal.saveButton') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import { maskMonthYear, monthYearError, monthYearToApi } from '@/utils/monthYear'
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'ProblemsPanel',
  mixins: [involvedPanelMixin, ApiErrorMixin],
  data() {
    return {
      catalog: [],
      records: [],
      loading: false,
      saving: false,
      cancellingId: null,
      showModal: false,
      showHistory: false,
      form: { problem_id: null, answer: null, since: '' },
      sinceTouched: false,
    }
  },
  computed: {
    active() {
      return this.records.filter(r => !r.cancelled_at)
    },
    history() {
      return this.records.filter(r => r.cancelled_at)
    },
    groups() {
      return [
        { key: 'tem', label: this.$t('involvedPanel.problems.table.tem'), items: this.active.filter(r => r.answer === true) },
        { key: 'nega', label: this.$t('involvedPanel.problems.table.nega'), items: this.active.filter(r => r.answer === false) },
      ].filter(g => g.items.length)
    },
    available() {
      const ativos = new Set(this.active.map(r => r.problem_id))
      return this.catalog.filter(p => !ativos.has(p.id))
    },
    currentMonth() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    },
    sinceError() {
      return monthYearError(this.form.since, this.currentMonth)
    },
    sinceMessage() {
      if (!this.sinceError) return null
      return this.sinceTouched || this.form.since.length === 7 ? this.sinceError : null
    },
    canSave() {
      return Boolean(this.form.problem_id)
        && (this.form.answer === true || this.form.answer === false)
        && !this.sinceError
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return this.load()
    },

    async load() {
      this.loading = true
      try {
        const [catalogRes, listRes] = await Promise.all([
          this.$http.get(`${this.$store.state.api}problems/for-occurrence/${this.occurrenceId}`),
          this.$http.get(`${this.$store.state.api}occurrence_involved_problems`, {
            params: {
              occurrence_id: Number(this.occurrenceId),
              involved_id: this.involvedId ? Number(this.involvedId) : undefined,
            },
          }),
        ])
        this.catalog = catalogRes.data.data || []
        this.records = listRes.data.data || []
      } catch (e) {
        this.catalog = []
        this.records = []
        this.$message(this.$t('involvedPanel.problems.messages.errorTitle'), this.apiErrorText(e, this.$t('involvedPanel.problems.messages.loadError')), 'error')
      } finally {
        this.loading = false
      }
    },
    openModal() {
      this.resetForm()
      this.showModal = true
    },
    resetForm() {
      this.form = { problem_id: null, answer: null, since: '' }
      this.sinceTouched = false
    },
    onSinceInput(e) {
      this.form.since = maskMonthYear(e.target.value)
      e.target.value = this.form.since
    },
    setAnswer(value) {
      this.form.answer = value
      if (!value) {
        this.form.since = ''
        this.sinceTouched = false
      }
    },
    problemLabel(r) {
      return r.problem ? r.problem.label : this.$t('involvedPanel.problems.table.removedProblem')
    },
    formatSince(value) {
      const [ano, mes] = String(value).split('-')
      return `${mes}/${ano}`
    },
    async save() {
      this.saving = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_involved_problems`, {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          problem_id: this.form.problem_id,
          answer: this.form.answer,
          since: this.form.answer ? monthYearToApi(this.form.since) : null,
        })
        this.records.unshift(res.data.data)
        this.showModal = false
        this.$message(this.$t('involvedPanel.problems.messages.saveSuccessTitle'), this.$t('involvedPanel.problems.messages.saveSuccessText'), 'success')
      } catch (e) {
        this.$message(this.$t('involvedPanel.problems.messages.errorTitle'), this.apiErrorText(e, this.$t('involvedPanel.problems.messages.saveError')), 'error')
      } finally {
        this.saving = false
      }
    },
    async cancel(r) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.problems.confirm.text'),
        {
          title: this.$t('involvedPanel.problems.confirm.title'),
          okTitle: this.$t('involvedPanel.problems.confirm.okTitle'),
          cancelTitle: this.$t('involvedPanel.problems.confirm.cancelTitle'),
          okVariant: 'danger',
          centered: true,
          size: 'sm',
        },
      )
      if (!confirmed) return
      this.cancellingId = r.id
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_involved_problems/${r.id}`)
        const idx = this.records.findIndex(x => x.id === r.id)
        if (idx !== -1) this.$set(this.records, idx, res.data.data)
      } catch (e) {
        this.$message(this.$t('involvedPanel.problems.messages.errorTitle'), this.apiErrorText(e, this.$t('involvedPanel.problems.messages.cancelError')), 'error')
      } finally {
        this.cancellingId = null
      }
    },
  },
}
</script>

<style scoped>
.prob-group {
  margin-top: 12px;
}

.prob-group__title {
  margin-bottom: 6px;
  padding-left: 8px;
  border-left: 3px solid;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.prob-group--tem .prob-group__title {
  border-color: #ea5455;
  color: #ea5455;
}

.prob-group--nega .prob-group__title {
  border-color: #82868b;
  color: #82868b;
}

.prob-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  margin-bottom: 6px;
  background: #fff;
}

.prob-group--tem .prob-item {
  border-left: 3px solid #ea5455;
}

.prob-group--nega .prob-item {
  border-left: 3px solid #b9b9c3;
}

.prob-item__body {
  min-width: 0;
}

.prob-item__head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.prob-item__name {
  font-weight: 600;
  color: #5e5873;
}

.prob-item__since {
  font-size: 12px;
  color: #6e6b7b;
}

.prob-history {
  margin-top: 14px;
  border-top: 1px dashed #ebe9f1;
  padding-top: 8px;
}

.prob-history__toggle {
  display: flex;
  align-items: center;
  padding: 4px 0;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #6e6b7b;
  cursor: pointer;
}

.prob-hist-item {
  padding: 6px 10px;
  margin-top: 6px;
  border-radius: 6px;
  background: #f8f8f8;
  opacity: 0.85;
}

.prob-tag {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
}

.prob-tag--tem {
  background: #ea5455;
}

.prob-tag--nega {
  background: #82868b;
}

.dark-layout .prob-item {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .prob-item__name {
  color: #d0d2d6;
}

.dark-layout .prob-item__since,
.dark-layout .prob-history__toggle {
  color: #b4b7bd;
}

.dark-layout .prob-history {
  border-top-color: #3b4253;
}

.dark-layout .prob-hist-item {
  background: #2f3349;
}
</style>
